<script setup>
// Composables
const { $bus } = useNuxtApp();
import { isPreviewing } from '@builder.io/sdk-vue'
import { runHubspotCompliance } from '@/composables/hubspot/compliance'
import { initializeWindowEvents } from '@/composables/helpers/layouts/window'
import { updateGlobals } from '@/composables/data/updateGlobals'

const route = useRoute()
const locked = ref(false)
const showSpeakerModal = ref(false)
const showAuthModal = ref(false)
const showReservationsModal = ref(false)
const showSearchModal = ref(false)

// Fetch Global Data
const { data: global } = await useAsyncData('all-global-data', () => $fetch('/api/global-data'));

if (process.env.NODE_ENV === 'production') {
    useHead({
        script: [
            {
                id: 'embedScript',
                src: '//js.hsforms.net/forms/embed/v2.js',
                type: 'text/javascript',
                defer: true,
            },
            {
                id: 'hubspot-compliance',
                src: 'https://www.hubspot.com/wt-assets/static-files/compliance/index.js',
                defer: true,
                type: 'text/javascript',
                'data-external-hs-domain': true,
                'data-gtm-id': 'GTM-T7GN22X',
                'data-pid': '146726',
                nonce: true
            }
        ],
    });
}

await updateGlobals(unref(global))

if (process.client) {
    initializeWindowEvents()
}

if (process.client && process.env.NODE_ENV === 'production') {
    if (!isPreviewing(route.fullPath)) await runHubspotCompliance()
}

watch(() => route.query, (newQuery) => {
    if (!process.client) return;
    setTimeout(() => {
        if (newQuery.drawer && /^\/speakers\/\w+$/.test(newQuery.drawer)) showSpeakerModal.value = true;
        else showSpeakerModal.value = false;
        if (newQuery.modal && /^\/login\/?$/.test(newQuery.modal)) showAuthModal.value = true;
        else showAuthModal.value = false;
        if (newQuery.modal && /^\/search\/?$/.test(newQuery.modal)) showSearchModal.value = true;
        else showSearchModal.value = false;
        if (newQuery.modal && /^\/reservations\/\w+$/.test(newQuery.modal)) showReservationsModal.value = true;
        else showReservationsModal.value = false;
        if (showSpeakerModal.value || showAuthModal.value || showReservationsModal.value || showSearchModal.value ) {
            if (process.client) document.documentElement.classList.add('locked');
        } else {
            if (process.client) document.documentElement.classList.remove('locked');
        }        
    }, 0);
}, { immediate: true })

const speaker_id = computed(() => {
    if (!route.query.drawer) return null;
    const match = String(route.query.drawer).match(/^\/speakers\/(.+)$/);
    return match ? match[1] : null;
});

$bus.on('drawer-open', () => {
    locked.value = true;
    if (process.client) { 
        document.documentElement.classList.add('locked');
    }
})

$bus.on('drawer-close', () => {
    locked.value = false;
    if (process.client) {
        document.documentElement.classList.remove('locked');
    }
})

</script>

<template>
    <UApp>
        <div id="app-wrapper" :class="{ 'locked': locked }">
            <NuxtLoadingIndicator />
            <NuxtRouteAnnouncer />
            <MessBlocker />
            <div class="root-wrapper">
                <div class="layout-wrapper">
                    <MessMetaActions />
                    <GlobalAnnouncement  />
                    <NuxtPage />
                    <FragmentsDialogsSearch :open="showSearchModal" />
                    <FragmentsDrawersSpeaker :open=showSpeakerModal :speaker_id="speaker_id" />                    
                    <FragmentsDialogsLogin :open="showAuthModal" />
                    <FragmentsDialogsReservations :open="showReservationsModal" />
                </div>
            </div>
        </div>
    </UApp>
</template>

<style lang="scss">
@import '@/assets/styles/main.scss';

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
    border-bottom: none !important;
}

body #hs-banner-parent div#hs-eu-cookie-confirmation {
    position: fixed !important;
    z-index: 9999;
    left: unset !important;
    right: unset !important;
    inset: unset !important;
}

body #hs-banner-parent.hs-banner-parent-fixed div#hs-eu-cookie-confirmation .hs-eu-cookie-close-button button {
    color: transparent !important;
}

#hs-eu-cookie-confirmation {
    // visibility: hidden;
}

#hs-eu-cookie-confirmation #hs-eu-close-button svg {
    color: white !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner {
    background: #213343 !important;
    box-sizing: content-box !important;
    margin: 0 auto !important;
    max-width: 1080px !important;
    padding: 0 2rem !important;
    gap: 0 !important;

    #hs-eu-cookie-settings-button {
        min-width: 8rem;
    }
}

#hs-banner-parent #hs-eu-cookie-confirmation {
    inset: 0 0 auto 50% !important;
    background: #213343 !important;
    padding: 8px !important;
    width: 100% !important;
    border-radius: 0 !important;
    max-width: unset !important;
}

#hs-banner-parent * {
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    box-shadow: none;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    display: inline-block;
    line-height: 1.75em;
}

div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
    letter-spacing: 0.2px;
}

#hs-eu-header-container {
    display: block !important;

    #hs-eu-close-button {
        position: absolute;
        top: 0;
        right: 10px;
        font-size: 32px;
        font-weight: 400;
        color: white;
        font-family: var(--lexend);
        line-height: 1.4rem;
    }
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area {
    margin-top: 2px !important;
}

div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
    margin: 0 72px 12px;
    color: #fff !important;
    font-family: inherit;
    font-size: 12px !important;
    font-weight: 400 !important;
    line-height: inherit;
    text-align: left;
    text-shadow: none !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
    font-size: 0.9rem !important;
    font-weight: 300 !important;
    line-height: 1.2rem !important;
    color: #fff !important;
    font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
    margin: 0 0 0.5rem !important;
    padding: 0 !important;
    margin-right: 0 !important;
    text-align: left !important;
}

#hs-eu-cookie-settings-button {
    font-family: var(--lexend) !important;
    font-weight: 500 !important;
    color: #fff !important;
    text-decoration: none !important;

    &:hover {
        text-decoration: underline !important;
    }
}

#hs-modal-accept-all,
#hs-modal-save-settings {
    text-align: center;
}

#hs-eu-cookie-confirmation #hs-eu-confirmation-button-group,
#hs-eu-cookie-confirmation #hs-eu-opt-in-buttons {
    gap: 1rem !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-confirmation-button,
#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-decline-button {
    background-color: #fff !important;
    border: 0 !important;
    border-radius: 0.1875rem !important;
    box-shadow: none !important;
    color: #2e475d !important;
    display: inline-block !important;
    line-height: 1.25 !important;
    text-shadow: none !important;
    transition: 0.15s color ease-out, 0.15s background-color ease-out, 0.15s border-color ease-out !important;
    font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
    font-weight: 500 !important;
    padding: 0 0.5rem !important;
    height: 3em !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button {
    max-width: 10rem !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area {
    justify-content: center !important;
    margin-bottom: 0.5rem !important;
    align-items: center !important;
    gap: 1rem !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation #hs-eu-cookie-confirmation-buttons-area {
    display: flex !important;
    margin-right: inherit !important;
    margin-top: 0 !important;
    position: relative !important;
    place-content: center !important;
    width: 100% !important;
}

#hs-eu-cookie-confirmation #hs-eu-cookie-confirmation-buttons-area {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-end;
    align-items: baseline;
}

#hs-banner-parent * {
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    box-shadow: none;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    display: inline-block;
    line-height: 1.75em;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
    font-size: 12.8px !important;
    padding-bottom: 6px !important;
    border-bottom: 1px solid #516f90;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area #hs-eu-confirmation-button-group #hs-eu-opt-in-buttons {
    -webkit-text-size-adjust: 100%;
    word-break: break-word;
    pointer-events: all !important;
    --hs-banner-accentColor: #715dce;
    --hs-banner-inset: 32px auto auto 50%;
    --hs-banner-translate-x: -50%;
    --hs-banner-translate-y: 0;
    --hs-banner-close-button-display: none;
    font-weight: 400;
    text-shadow: none !important;
    -webkit-font-smoothing: antialiased;
    font-size: inherit;
    font-family: inherit;
    background: none;
    border: none;
    box-shadow: none;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    line-height: 1.75em;
    color: inherit;
    display: flex;
    justify-content: center;
    flex-flow: row wrap;
    flex-grow: 1;
    gap: 1rem !important;
}

#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-confirmation-button,
#hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-decline-button {
    text-align: center !important;
}

#hs-eu-policy-wording>p>a {
    text-decoration: underline !important;
    font-weight: 500 !important;
    font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
    font-size: 14.4px !important;
    color: #fff !important;
    line-height: inherit !important;
}

@media (max-width: 1152px) {
    #hs-banner-parent.hs-banner-parent-fixed div#hs-eu-cookie-confirmation #hs-eu-policy-wording p {
        margin-right: 2.5rem !important;
    }
}

@media (max-width: 800px) {
    #hs-banner-parent #hs-eu-cookie-confirmation {
        padding: 26px 8px 8px !important;

        #hs-eu-cookie-settings-button {
            line-height: 1.8rem;
            max-width: 9rem !important;
            min-width: 9rem !important;
        }
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner {
        padding: 0 !important;
    }

    #hs-banner-parent p {
        font-size: 0.8rem !important;
    }

    #hs-eu-cookie-confirmation-buttons-area button {
        font-size: 1rem !important;
    }
}

@media (max-width: 450px) {
    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-cookie-settings-button {
        width: unset !important;
        text-align: center;
        height: 1.6rem;
    }
}
</style>
